/**
 * Ask Dink card (DNKADM-0461) — the per-business switch for the top-bar how-to assistant.
 *
 * One switch that saves ON ITS OWN when flipped: `PUT /v2/admin/tenant-settings` carrying
 * only `assistantEnabled` (partial update). Not optimistic — the switch shows the value
 * from the PUT response, so a failed PUT leaves the previous value showing and raises the
 * server's message through `classifyFormError` / `serverMessage`. It does not refetch the
 * screen (that would remount the booking form and drop unsaved edits). After a successful
 * save it asks the top-bar launcher to re-read `GET /v2/admin/assistant/status`, so the
 * button appears or disappears without a reload. The route is `RequireAdmin`-gated (OWNER
 * and ADMIN); STAFF never reach this screen.
 */
import { useState } from 'react';
import { Box, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { Icon } from '@/theme';
import type { ShowToast } from '@/components/toastContext';
import { getAssistantStatus } from '@/api/assistant';
import { putTenantSettings } from '@/api/tenantSettings';
import { classifyFormError } from '@/api/screenError';
import { requestAssistantStatusRefresh } from '@/features/ask-dink/assistantStatusRefresh';

interface AskDinkCardProps {
  /** The loaded `assistantEnabled` value. */
  initialEnabled: boolean;
  /** Raise a non-blocking toast. */
  showToast: ShowToast;
}

const SWITCH_LABEL = 'Turn on Ask Dink for this business';

/**
 * DNKADM-0474: shown after a save turned Ask Dink on and the status read still answers off.
 * The status is the platform flag AND this switch (DNKAPI-0665), so the platform side is off.
 */
const PLATFORM_OFF_HINT =
  'Ask Dink is on for your business, but it is not switched on for the platform yet. The button stays hidden until it is.';

export function AskDinkCard({ initialEnabled, showToast }: AskDinkCardProps) {
  // The SAVED value; only a PUT response moves it.
  const [enabled, setEnabled] = useState(initialEnabled);
  const [saving, setSaving] = useState(false);
  const [platformOff, setPlatformOff] = useState(false);

  async function handleChange(next: boolean) {
    setSaving(true);
    try {
      const saved = await putTenantSettings({ assistantEnabled: next });
      setEnabled(saved.assistantEnabled);
      showToast(saved.assistantEnabled ? 'Ask Dink turned on' : 'Ask Dink turned off', 'success');
      requestAssistantStatusRefresh();
      // A failed status read says nothing, so it never raises the hint.
      setPlatformOff(
        saved.assistantEnabled ? await getAssistantStatus().then((on) => !on, () => false) : false,
      );
    } catch (err) {
      const view = classifyFormError(err, 'That value isn’t valid.', {
        toastFallback: 'The settings couldn’t be saved. Please try again.',
        conflictInline: false,
      });
      showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
            p: '16px 20px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: 'background.default',
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <Icon name="support_agent" sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Ask Dink</Typography>
        </Stack>
        <Box sx={{ p: '18px 20px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event) => void handleChange(event.target.checked)}
                disabled={saving}
              />
            }
            label={SWITCH_LABEL}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Your team gets a how-to assistant in the top bar. Answers come from the Dink help guide.
            It never changes your data.
          </Typography>
          {enabled && platformOff && (
            <Typography variant="body2" role="status" sx={{ color: 'warning.main', mt: 1 }}>
              {PLATFORM_OFF_HINT}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
